/**
 * «Голосовой стенд» — разговор с realtime-моделями (OpenAI, Gemini, Яндекс) по сценарию обзвона сделки
 * «В работе»: в браузере голосом или настоящим звонком на телефон (LiveKit SIP → FreePBX voip-2, только номера
 * из списка). Выбор модели, голоса и режима, карточка «клиента», живая лента реплик с паузой робота и расходом;
 * вкладка «Журнал» (`?tab=calls&call=<комната>`) — все разговоры с записью звука. Право `voice_lab`.
 * Спека `docs/specs/2026-10-26-voice-lab-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { Room, RoomEvent, Track } from "livekit-client";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { VOICE_LAB, useCan } from "@/lib/access";
import {
  INTERESTS,
  SPEEDS,
  type LabCost,
  type LabEvent,
  MODES,
  OUTCOMES,
  labCall,
  labCallState,
  labHangup,
  labOptionsQuery,
  labToken,
} from "@/lib/voiceLab";

import { VoiceLabDeal } from "./VoiceLabDeal";
import { VoiceLabJournal, телефон } from "./VoiceLabJournal";
import { VoiceLabScenarios } from "./VoiceLabScenarios";
import { RemarksPage } from "./VoiceLabRemarks";
import { VoiceLabProblems } from "./VoiceLabProblems";
import { VoiceLabPrompt } from "./VoiceLabPrompt";
import { VoiceLabSelect } from "./VoiceLabSelect";
import { CostBlock, Transcript } from "./VoiceLabParts";
import { VoiceLabPhrases } from "./VoiceLabPhrases";
import { VoiceLabMarin, VoiceLabVoices } from "./VoiceLabVoices";

const control =
  "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] text-fg";
const tabCls = (on: boolean) =>
  `shrink-0 rounded-full border px-3 py-1 text-[12.5px] ${on ? "border-accent bg-accent/10 text-fg" : "border-border text-fg-muted hover:text-fg"}`;
const ЗВОНОК_ИДЁТ = 11 * 60_000; // мс — дольше разговор не длится (лимит агента 10 мин + гудки)
const КОМНАТА_ПОЯВИТСЯ = 30_000; // мс — после dispatch комнаты может ещё не быть: «нет» не значит «закончен»

/** Сегодня по часам браузера (не UTC: после 21:00 МСК `toISOString` уже завтра). */
function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function VoiceLabPage() {
  const can = useCan(VOICE_LAB);
  const [params, setParams] = useSearchParams();
  const tab =
    (["calls", "remarks", "scenarios", "problems", "voices", "marin", "phrases", "prompt", "select"] as const).find(
      (t) => t === params.get("tab"),
    ) ?? "talk";
  const options = useQuery({ ...labOptionsQuery(), enabled: can });
  const [model, setModel] = useState("");
  const [voice, setVoice] = useState("");
  const [mode, setMode] = useState("faq");
  const [name, setName] = useState("Анна");
  const [interest, setInterest] = useState(INTERESTS[0]!);
  const [speed, setSpeed] = useState(1.05);
  const [location, setLocation] = useState<number | null>(null);
  const [requestDate, setRequestDate] = useState(today());
  const [phone, setPhone] = useState("");
  const [crmPhone, setCrmPhone] = useState("");
  const [turn, setTurn] = useState<"server" | "livekit">("server");
  const [events, setEvents] = useState<LabEvent[]>([]);
  const [state, setState] = useState<"idle" | "connecting" | "live" | "ended">(
    "idle",
  );
  const [error, setError] = useState<string | null>(null);
  const [calling, setCalling] = useState<{
    phone: string;
    room: string;
    since: number;
  } | null>(null);
  const [dialing, setDialing] = useState(false); // запрос «Позвонить» в пути — второй клик не звонит
  const room = useRef<Room | null>(null);
  const audio = useRef<HTMLDivElement>(null);
  // Поколение запуска: «Завершить» или уход со страницы, пока ждём микрофон/токен/комнату, — поздний
  // результат старого запуска сразу закрываем, а не включаем микрофон.
  const запуск = useRef(0);

  const models = options.data?.models ?? [];
  const chosen = model || models.find((m) => m.available)?.code || "";
  const м = models.find((x) => x.code === chosen);
  const голос = voice && м?.voices.includes(voice) ? voice : (м?.voice ?? "");
  const loc = location ?? options.data?.locations[0]?.id ?? null;
  const phones = options.data?.phones ?? [];
  const номер = phone || phones[0] || "";
  const состояниеЗвонка = useQuery({
    queryKey: ["voice-lab-call", calling?.room],
    queryFn: () => labCallState(calling!.room),
    enabled: Boolean(calling),
    refetchInterval: 5_000,
  });
  const виделиЗвонок = useRef(false);
  if (состояниеЗвонка.data?.active) виделиЗвонок.current = true;
  const звонокКончился =
    calling != null &&
    состояниеЗвонка.data?.active === false &&
    (виделиЗвонок.current || Date.now() - calling.since > КОМНАТА_ПОЯВИТСЯ);
  const звонок =
    calling && !звонокКончился && Date.now() - calling.since < ЗВОНОК_ИДЁТ
      ? calling
      : null;

  useEffect(
    () => () => {
      запуск.current++;
      void room.current?.disconnect();
    },
    [],
  );

  const openai = chosen.startsWith("openai");
  const заявка = () => ({
    model: chosen,
    voice: голос,
    mode,
    name,
    interest,
    location_id: loc!,
    request_date: requestDate,
    ...(openai ? { speed } : {}),
    ...(crmPhone.trim() ? { crm_phone: crmPhone.trim() } : {}),
    ...(openai ? { turn } : {}),
  });

  async function start() {
    if (!chosen || loc == null) return;
    const моё = ++запуск.current;
    const актуально = () => запуск.current === моё;
    setError(null);
    setEvents([]);
    setState("connecting");
    let r: Room | null = null;
    try {
      // Микрофон — до комнаты: иначе без него агент войдёт в комнату и будет ждать впустую.
      try {
        const probe = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        probe.getTracks().forEach((tr) => tr.stop());
      } catch {
        throw new Error(
          "Нет доступа к микрофону — подключите его и разрешите сайту доступ",
        );
      }
      if (!актуально()) return;
      const t = await labToken(заявка());
      if (!актуально()) return;
      r = new Room();
      room.current = r;
      r.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind === Track.Kind.Audio)
          audio.current?.appendChild(track.attach());
      });
      r.on(RoomEvent.TrackUnsubscribed, (track) =>
        track.detach().forEach((el) => el.remove()),
      );
      r.on(RoomEvent.DataReceived, (payload, _p, _k, topic) => {
        if (topic !== "lab") return;
        const ev = JSON.parse(new TextDecoder().decode(payload)) as LabEvent;
        setEvents((prev) => [...prev, ev]);
        if (ev.type === "end") setState("ended");
      });
      r.on(RoomEvent.Disconnected, () =>
        setState((s) => (s === "live" ? "ended" : s)),
      );
      await r.connect(t.url, t.token);
      if (!актуально()) {
        await r.disconnect();
        return;
      }
      await r.localParticipant.setMicrophoneEnabled(true);
      if (!актуально()) {
        await r.disconnect();
        return;
      }
      setState("live");
      // Агент — процесс вне веба: не запущен — в комнате тишина, а «идёт разговор» горит.
      const эта = r;
      setTimeout(() => {
        if (room.current !== эта || эта.remoteParticipants.size > 0) return;
        setError("Робот не вошёл в комнату за 10 с — агент стенда не запущен");
        void stop();
      }, 10_000);
    } catch (e) {
      await r?.disconnect(); // своя комната этого запуска, не чужая из room.current
      if (room.current === r) room.current = null;
      if (!актуально()) return;
      setError(e instanceof Error ? e.message : String(e));
      setState("idle");
    }
  }

  async function stop() {
    запуск.current++;
    const r = room.current;
    room.current = null;
    await r?.disconnect();
    if (audio.current) audio.current.innerHTML = "";
    setState("ended");
  }

  async function call() {
    if (!chosen || loc == null || !номер || dialing) return;
    setError(null);
    setDialing(true);
    try {
      const r = await labCall({ ...заявка(), phone: номер });
      виделиЗвонок.current = false;
      setCalling({ phone: r.phone, room: r.room, since: Date.now() });
      setParams({ tab: "calls" });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDialing(false);
    }
  }

  async function hangup() {
    if (!calling) return;
    try {
      await labHangup(calling.room);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    void состояниеЗвонка.refetch();
  }

  if (!can)
    return (
      <main className="p-5 text-[13px] text-fg-muted">
        Раздел «Голосовой стенд» — по праву «Голосовой стенд».
      </main>
    );

  const turns = events.filter((e) => e.type === "turn" || e.type === "tool");
  const pauses = events.flatMap((e) =>
    e.type === "turn" && e.who === "agent" && e.latency_ms
      ? [e.latency_ms]
      : [],
  );
  const avg = pauses.length
    ? Math.round(pauses.reduce((a, b) => a + b, 0) / pauses.length)
    : null;
  const end = events.find((e) => e.type === "end");
  const cost = events.reduce<LabCost | null>(
    (c, e) => (e.type === "cost" || e.type === "end" ? (e.cost ?? c) : c),
    null,
  );
  const busy = state === "connecting" || state === "live";
  const готово = Boolean(chosen && loc != null && name.trim());

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div
        className="flex flex-wrap items-center gap-3"
        data-annot="шапка стенда"
      >
        <h1 className="text-[16px] font-semibold tracking-[-0.015em]">
          Голосовой стенд
        </h1>
        <nav className="flex gap-1.5">
          <button
            type="button"
            className={tabCls(tab === "talk")}
            onClick={() => setParams({})}
          >
            Разговор
          </button>
          <button
            type="button"
            className={tabCls(tab === "calls")}
            onClick={() => setParams({ tab: "calls" })}
          >
            Журнал
          </button>
          <button
            type="button"
            className={tabCls(tab === "remarks")}
            onClick={() => setParams({ tab: "remarks" })}
          >
            Замечания
          </button>
          <button
            type="button"
            className={tabCls(tab === "scenarios")}
            onClick={() => setParams({ tab: "scenarios" })}
          >
            Сценарии
          </button>
          {/* Шаблоны AI — готовые ответы черновиков «Чатов» по частым вопросам; живут в разделе «Шаблоны». */}
          <Link to="/templates?tab=ai" className={tabCls(false)} title="Готовые ответы робота «Чатов» на частые вопросы">
            Шаблоны AI ↗
          </Link>
          <button
            type="button"
            className={tabCls(tab === "problems")}
            onClick={() => setParams({ tab: "problems" })}
          >
            Задачи робота
          </button>
          <button
            type="button"
            className={tabCls(tab === "voices")}
            onClick={() => setParams({ tab: "voices" })}
          >
            Голоса
          </button>
          <button
            type="button"
            className={tabCls(tab === "marin")}
            onClick={() => setParams({ tab: "marin" })}
          >
            Марин · скорость
          </button>
          <button
            type="button"
            className={tabCls(tab === "phrases")}
            onClick={() => setParams({ tab: "phrases" })}
          >
            Библиотека
          </button>
          <button
            type="button"
            className={tabCls(tab === "prompt")}
            onClick={() => setParams({ tab: "prompt" })}
          >
            Инструкция
          </button>
          <button
            type="button"
            className={tabCls(tab === "select")}
            onClick={() => setParams({ tab: "select" })}
          >
            Подбор
          </button>
        </nav>
        {звонок && (
          <span className="flex items-center gap-2 text-[12.5px] text-accent">
            ☎ звоним на {телефон(звонок.phone)}…
            <button
              type="button"
              onClick={hangup}
              className="rounded-[var(--radius-ui)] border border-err px-2 py-0.5 text-[12px] text-err"
            >
              Положить трубку
            </button>
          </span>
        )}
        {busy && tab !== "talk" && (
          <span className="flex items-center gap-2 text-[12.5px] text-accent">
            ● идёт разговор
            <button
              type="button"
              onClick={stop}
              className="rounded-[var(--radius-ui)] border border-err px-2 py-0.5 text-[12px] text-err"
            >
              Завершить
            </button>
          </span>
        )}
      </div>
      {/* Звук робота — вне вкладок: переход в «Журнал» во время разговора не глушит его */}
      <div ref={audio} className="hidden" />
      {tab === "remarks" ? (
        <RemarksPage />
      ) : tab === "scenarios" ? (
        <VoiceLabScenarios />
      ) : tab === "problems" ? (
        <VoiceLabProblems />
      ) : tab === "select" ? (
        <VoiceLabSelect cities={options.data?.locations ?? []} />
      ) : tab === "prompt" ? (
        <VoiceLabPrompt
          query={
            chosen && loc != null
              ? {
                  model: chosen,
                  mode,
                  location_id: loc,
                  name,
                  interest,
                  request_date: requestDate,
                }
              : null
          }
          label={`${м?.label ?? chosen} · ${options.data?.locations.find((l) => l.id === loc)?.name ?? "—"} · ${name}, ${interest}`}
          setMode={setMode}
        />
      ) : tab === "phrases" ? (
        <VoiceLabPhrases />
      ) : tab === "marin" ? (
        <VoiceLabMarin
          chosen={chosen === "openai" && голос === "marin" ? speed : null}
          pick={(sp) => {
            setModel("openai");
            setVoice("marin");
            setSpeed(sp);
            setParams({});
          }}
        />
      ) : tab === "voices" ? (
        <VoiceLabVoices
          chosen={{ model: chosen, voice: голос }}
          pick={(m, v) => {
            setModel(m);
            setVoice(v);
            setParams({});
          }}
        />
      ) : tab === "calls" ? (
        <VoiceLabJournal
          room={params.get("call")}
          version={params.get("version")}
          open={(r) => {
            // отбор «разговоры на этой версии» (вкладка «Сценарии») переживает открытие карточки
            const v = params.get("version");
            setParams({ tab: "calls", ...(r ? { call: r } : {}), ...(v ? { version: v } : {}) });
          }}
          clearVersion={() => setParams({ tab: "calls" })}
          watching={Boolean(звонок)}
        />
      ) : (
        <>
          <div
            className="flex flex-wrap items-end gap-3 text-[12.5px]"
            data-annot="настройки разговора"
          >
            <label className="flex flex-col gap-1">
              Модель
              <select
                value={chosen}
                disabled={busy}
                onChange={(e) => setModel(e.target.value)}
                className={control}
              >
                {models.map((m) => (
                  <option key={m.code} value={m.code} disabled={!m.available}>
                    {m.label}
                    {m.available ? "" : " — нет ключа"}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              Голос
              <select
                value={голос}
                disabled={busy || !м}
                onChange={(e) => setVoice(e.target.value)}
                className={control}
              >
                {м?.voices.map((v) => (
                  <option key={v} value={v}>
                    {v}
                    {v === м.voice ? " — по умолчанию" : ""}
                  </option>
                ))}
              </select>
            </label>
            {openai && (
              <label className="flex flex-col gap-1">
                Скорость
                <select
                  value={speed}
                  disabled={busy}
                  onChange={(e) => setSpeed(Number(e.target.value))}
                  className={control}
                >
                  {SPEEDS.map((v) => (
                    <option key={v} value={v}>
                      ×{v.toFixed(2)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {openai && (
              <label
                className="flex flex-col gap-1"
                title="Кто решает, что собеседник договорил и перебил робота. «LiveKit» — свой детектор речи и adaptive-перебивания: эхо робота в трубке и «угу» не обрывают его фразу."
              >
                Перебивания
                <select
                  value={turn}
                  disabled={busy}
                  onChange={(e) =>
                    setTurn(e.target.value as "server" | "livekit")
                  }
                  className={control}
                >
                  <option value="server">модель</option>
                  <option value="livekit">LiveKit</option>
                </select>
              </label>
            )}
            <label className="flex flex-col gap-1">
              Режим
              <select
                value={mode}
                disabled={busy}
                onChange={(e) => setMode(e.target.value)}
                className={control}
              >
                {(options.data?.modes ?? ["faq", "script", "kb"]).map((m) => (
                  <option key={m} value={m}>
                    {MODES[m] ?? m}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              Имя клиента
              <input
                value={name}
                maxLength={60}
                disabled={busy}
                onChange={(e) => setName(e.target.value)}
                className={`${control} w-36`}
              />
            </label>
            <label
              className="flex flex-col gap-1"
              title="Запись, перезвон и отказ робот делает по этому номеру: сделка или лид «В работе». В Bitrix пишет только при PROKAT_VOICE_LAB_CRM_WRITE, иначе — предпросмотр в журнале."
            >
              Номер в CRM
              <input
                value={crmPhone}
                maxLength={20}
                disabled={busy}
                placeholder="номер звонка"
                onChange={(e) => setCrmPhone(e.target.value)}
                className={`${control} w-36`}
              />
            </label>
            <label className="flex flex-col gap-1">
              Что искала
              <select
                value={interest}
                disabled={busy}
                onChange={(e) => setInterest(e.target.value)}
                className={control}
              >
                {INTERESTS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              Салон
              <select
                value={loc ?? ""}
                disabled={busy}
                onChange={(e) => setLocation(Number(e.target.value))}
                className={control}
              >
                {options.data?.locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              Дата заявки
              <input
                type="date"
                value={requestDate}
                disabled={busy}
                onChange={(e) => setRequestDate(e.target.value)}
                className={control}
              />
            </label>
            {busy ? (
              <button
                type="button"
                onClick={stop}
                className="h-8 rounded-[var(--radius-ui)] bg-err px-3 text-[12.5px] font-medium text-white"
              >
                Завершить
              </button>
            ) : (
              <button
                type="button"
                onClick={start}
                disabled={!готово}
                className="h-8 rounded-[var(--radius-ui)] bg-accent px-3 text-[12.5px] font-medium text-white disabled:opacity-50"
              >
                Начать разговор
              </button>
            )}
            <span className="text-[12px] text-fg-muted" data-annot="состояние">
              {state === "connecting"
                ? "подключаемся…"
                : state === "live"
                  ? "● идёт разговор"
                  : end
                    ? `итог: ${OUTCOMES[end.outcome] ?? end.outcome}`
                    : ""}
            </span>
          </div>
          <VoiceLabDeal
            body={
              chosen
                ? {
                    model: chosen,
                    voice: голос,
                    mode,
                    ...(openai ? { speed, turn } : {}),
                  }
                : null
            }
            disabled={busy || Boolean(звонок)}
            onCalled={(r) => {
              виделиЗвонок.current = false;
              setCalling({ phone: r.phone, room: r.room, since: Date.now() });
              setParams({ tab: "calls" });
            }}
          />
          {phones.length > 0 && (
            <div
              className="flex flex-wrap items-end gap-3 text-[12.5px]"
              data-annot="звонок на телефон"
            >
              <label className="flex flex-col gap-1">
                Позвонить на телефон
                <select
                  value={номер}
                  disabled={busy || dialing || Boolean(звонок)}
                  onChange={(e) => setPhone(e.target.value)}
                  className={control}
                >
                  {phones.map((p) => (
                    <option key={p} value={p}>
                      {телефон(p)}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={call}
                disabled={!готово || busy || dialing || Boolean(звонок)}
                className="h-8 rounded-[var(--radius-ui)] border border-accent px-3 text-[12.5px] font-medium text-accent disabled:opacity-50"
              >
                Позвонить
              </button>
              <span className="text-[12px] text-fg-muted">
                робот звонит сам с +7 926 927-01-51 — та же модель, голос и
                сценарий; разговор и запись — в «Журнале»
              </span>
            </div>
          )}
          {error && <div className="text-[12.5px] text-err">{error}</div>}
          <Transcript
            items={
              turns as Extract<LabEvent, { type: "turn" } | { type: "tool" }>[]
            }
          />
          {avg != null && (
            <div
              className="text-[12.5px] text-fg-muted"
              data-annot="сводка разговора"
            >
              Реплик робота с замером: {pauses.length} · средняя пауза {avg} мс
              · пауза — от конца вашей речи до начала звука робота на сервере
            </div>
          )}
          {cost && <CostBlock cost={cost} live={busy} />}
        </>
      )}
    </main>
  );
}
